/* eslint-disable max-lines */
import { AlarmGroupCell } from '@hakimo-ui/hakimo/feature-shared';
import {
  CamAction,
  CurrentAndSiblingEscalations,
  LLMEvent,
  UnifiedAlarmGroup,
  UnifiedMonitoringAlarmsMessage,
  UnifiedMonitoringLLMRecommendationMessage,
  UnifiedMonitoringMessageType,
} from '@hakimo-ui/hakimo/types';
import { DynamicGrid, DynamicGridRef } from '@hakimo-ui/hakimo/ui-elements';
import {
  buildDoorToCameraMap,
  remapAlarmToCamera,
  toast,
  trackEvent,
  useUnifiedMonitoringWs,
} from '@hakimo-ui/hakimo/util';
import { useEffect, useMemo, useRef, useState } from 'react';
import { calculateGridConfig, getCurrentAndSiblingEscalations } from '../utils';
import BulkResolveBanner from './BulkResolveBanner';
import { FETCH_ALARMS_DELTA_POLLING_INTERVAL } from './constants';
import { useUnifiedMonitoringWsMessageHandler } from './hooks/useUnifiedMonitoringWsMessageHandler';

interface Props {
  alarmGroupIdToDetailsMap: Record<string, UnifiedAlarmGroup>;
  totalAlarmCount: number;
  onAlarmGroupAction: (
    actionType: CamAction,
    alarmGroupId: string,
    comment?: string,
    cameraId?: string,
    cameraGroupId?: string
  ) => void;
  navigateToPendingAlarms: () => void;
}

export function MultiAlarmGroupView(props: Props) {
  const {
    alarmGroupIdToDetailsMap,
    totalAlarmCount,
    onAlarmGroupAction,
    navigateToPendingAlarms,
  } = props;

  const [updatedAlarmGroupIdToDetailsMap, setUpdatedAlarmGroupIdToDetailsMap] =
    useState<Record<string, UnifiedAlarmGroup>>(() => alarmGroupIdToDetailsMap);

  const alarmGroupIdToDetailsMapRef = useRef<Record<string, UnifiedAlarmGroup>>(
    alarmGroupIdToDetailsMap
  );
  const dynamicGridRef = useRef<DynamicGridRef>(null);
  const [alarmEscalationMap, setAlarmEscalationMap] = useState<
    Record<string, CurrentAndSiblingEscalations>
  >({});
  const isInitialMessageSentForLLMRecommendationRef = useRef(false);

  const { send } = useUnifiedMonitoringWs();

  useEffect(() => {
    try {
      const alarmGroupIds = Object.keys(alarmGroupIdToDetailsMapRef.current);
      const dynamicGridRefCurrent = dynamicGridRef.current;
      const currentItems = dynamicGridRefCurrent?.getAllItems();
      const itemsToAdd = alarmGroupIds.filter(
        (alarmGroupId) => !currentItems?.includes(alarmGroupId)
      );
      if (itemsToAdd.length > 0) {
        dynamicGridRefCurrent?.addItems(itemsToAdd);
      }
    } catch (error) {
      trackEvent('scan_error_adding_alarm_group', {
        error: error,
      });
    }
  }, []);

  const sendMessageForLLMRecommendation = () => {
    const alarmGroupIdToDetailsMapData = alarmGroupIdToDetailsMapRef.current;
    Object.values(alarmGroupIdToDetailsMapData).forEach((alarmGroup) => {
      send?.({
        event_type: UnifiedMonitoringMessageType.UNIFIED_ALARMS,
        alarm_group_id: alarmGroup.id,
        product: alarmGroup.product,
        tenant_id: alarmGroup.tenantId,
        existing_unified_alarm_ids: Array.from(alarmGroup.uniqueAlarmIds ?? []),
        alarm_timestamp_sec: new Date(alarmGroup.timestamp).getTime() / 1000,
        // Omitted rather than `false` for v2, so a legacy frame stays
        // byte-identical to a pre-unification one (same rule as url-params).
        unification_enabled: alarmGroup.unificationEnabled || undefined,
        partition_key: alarmGroup.unificationEnabled
          ? alarmGroup.partition_key
          : undefined,
      });

      // llm recommendation
      send?.({
        event_type: UnifiedMonitoringMessageType.LLM_RECOMMENDATION,
        alarm_group_id: alarmGroup.id,
        product: alarmGroup.product,
        unification_enabled: alarmGroup.unificationEnabled || undefined,
        partition_key: alarmGroup.unificationEnabled
          ? alarmGroup.partition_key
          : undefined,
      });
    });
  };

  useEffect(() => {
    if (isInitialMessageSentForLLMRecommendationRef.current === false) {
      sendMessageForLLMRecommendation();
      isInitialMessageSentForLLMRecommendationRef.current = true;
    }
    const timer = window.setInterval(() => {
      sendMessageForLLMRecommendation();
    }, FETCH_ALARMS_DELTA_POLLING_INTERVAL);

    return () => {
      timer && clearInterval(timer);
    };
  }, [send]);

  const handleUnifiedAlarmsResponse = (
    allData: UnifiedMonitoringAlarmsMessage[]
  ) => {
    const alarmGroupIdToDetailsMapData = {
      ...alarmGroupIdToDetailsMapRef.current,
    };
    const updatedAlarmEscalationMap = { ...alarmEscalationMap };
    allData.forEach((data) => {
      const {
        alarms: newAlarms,
        alarm_group_id: alarmGroupId,
        escalations,
      } = data.payload;

      if (!alarmGroupIdToDetailsMapData[alarmGroupId]) {
        return;
      }
      const alarmGroupData = alarmGroupIdToDetailsMapData[alarmGroupId];
      const uniqueAlarmIds = alarmGroupData.uniqueAlarmIds ?? new Set<string>();
      // Learn from the new batch too: it can bring a door's camera copy after
      // a door-only copy of the same door.
      const doorToCamera = buildDoorToCameraMap([
        ...alarmGroupData.alarms,
        ...newAlarms,
      ]);
      newAlarms.forEach((newAlarm) => {
        const alarm = remapAlarmToCamera(newAlarm, doorToCamera);
        if (uniqueAlarmIds.has(alarm.id)) {
          return;
        }
        uniqueAlarmIds.add(alarm.id);
        alarmGroupData.alarms = [...alarmGroupData.alarms, alarm];
        alarmGroupData.alarmCount = alarmGroupData.alarms.length;
        if (alarmGroupData.camToAlarmsMap?.has(alarm.cameraId)) {
          alarmGroupData.camToAlarmsMap.get(alarm.cameraId)?.push(alarm);
        } else {
          alarmGroupData.camToAlarmsMap?.set(alarm.cameraId, [alarm]);
        }
      });
      alarmGroupData.escalations = escalations;
      alarmGroupData.uniqueAlarmIds = uniqueAlarmIds;
      alarmGroupIdToDetailsMapData[alarmGroupId] = alarmGroupData;

      // update current and sibling escalation data
      const { currentAlarmEscalation, siblingEscalations } =
        getCurrentAndSiblingEscalations(escalations, {
          alarmGroupId,
          sourceGroupId: alarmGroupData.sourceGroupId,
          locationAlarmTypeId: alarmGroupData.locationAlarmTypeId,
        });
      updatedAlarmEscalationMap[alarmGroupId] = {
        currentAlarmEscalation,
        siblingEscalations,
      };
    });
    alarmGroupIdToDetailsMapRef.current = alarmGroupIdToDetailsMapData;
    setUpdatedAlarmGroupIdToDetailsMap(alarmGroupIdToDetailsMapRef.current);
    setAlarmEscalationMap(updatedAlarmEscalationMap);
  };

  useUnifiedMonitoringWsMessageHandler(
    UnifiedMonitoringMessageType.UNIFIED_ALARMS,
    handleUnifiedAlarmsResponse
  );

  const handleLLMRecommendation = (
    allData: UnifiedMonitoringLLMRecommendationMessage[]
  ) => {
    const alarmGroupIdToDetailsMapData = {
      ...alarmGroupIdToDetailsMapRef.current,
    };
    allData.forEach((data) => {
      const {
        alarm_group_id: alarmGroupId,
        recommendation,
        summary,
        headline,
      } = data.payload;
      alarmGroupIdToDetailsMapData[alarmGroupId].llmRecommendation = {
        headline,
        recommendation,
        summary,
      };
    });
    alarmGroupIdToDetailsMapRef.current = alarmGroupIdToDetailsMapData;
    setUpdatedAlarmGroupIdToDetailsMap(alarmGroupIdToDetailsMapRef.current);
  };

  useUnifiedMonitoringWsMessageHandler(
    UnifiedMonitoringMessageType.LLM_RECOMMENDATION,
    handleLLMRecommendation
  );

  const gridConfig = useMemo(() => {
    return calculateGridConfig(totalAlarmCount);
  }, [totalAlarmCount]);

  const handleSafeForAlarmGroupIds = (alarmGroupIds: string[]) => {
    dynamicGridRef.current?.removeItems(alarmGroupIds);
    const alarmGroupIdToDetailsMapData = {
      ...alarmGroupIdToDetailsMapRef.current,
    };
    alarmGroupIds.forEach((alarmGroupId) => {
      delete alarmGroupIdToDetailsMapData[alarmGroupId];
    });
    alarmGroupIdToDetailsMapRef.current = alarmGroupIdToDetailsMapData;
    setUpdatedAlarmGroupIdToDetailsMap(alarmGroupIdToDetailsMapRef.current);

    if (Object.keys(alarmGroupIdToDetailsMapData).length === 0) {
      navigateToPendingAlarms();
    }
  };

  const onAlarmGroupCellAction = (
    cameraId: string,
    alarmGroupId: string,
    cameraGroupId: string,
    actionType: CamAction,
    comment?: string
  ) => {
    // investigate, safe,

    if (actionType === CamAction.INVESTIGATE) {
      // send orphan event for all other alarm groups
      let orphanAlarmCount = 0;
      let alreadyResolvedAlarmCount = 0;
      Object.keys(updatedAlarmGroupIdToDetailsMap).forEach((itemId) => {
        if (itemId !== alarmGroupId) {
          const item = updatedAlarmGroupIdToDetailsMap[itemId];
          const isAlreadyResolved = item.status
            .toLowerCase()
            .includes('resolved');
          if (isAlreadyResolved) {
            alreadyResolvedAlarmCount++;
            return;
          }
          send?.({
            event_type: UnifiedMonitoringMessageType.ORPHAN_ALARM,
            alarm_group_id: itemId,
            product: item.product,
            unification_enabled: item.unificationEnabled || undefined,
            partition_key: item.unificationEnabled
              ? item.partition_key
              : undefined,
          });
          orphanAlarmCount++;
        }
      });
      if (orphanAlarmCount > 0) {
        toast(`${orphanAlarmCount} alarms are added back to the queue`);
      }
      if (alreadyResolvedAlarmCount > 0) {
        toast(
          `${alreadyResolvedAlarmCount} alarms are resolved alarms so just removed them`
        );
      }
    }

    if (actionType === CamAction.SAFE) {
      handleSafeForAlarmGroupIds([alarmGroupId]);
    }

    onAlarmGroupAction(
      actionType,
      alarmGroupId,
      comment,
      cameraId,
      cameraGroupId
    );
  };

  const getLLMEvent = (alarmGroupId: string): LLMEvent | undefined => {
    const alarmGroup = updatedAlarmGroupIdToDetailsMap[alarmGroupId];
    if (!alarmGroup.llmRecommendation) {
      return undefined;
    }
    return {
      alarm_group_id: alarmGroup.id,
      camera_group_id: '',
      event_type: '',
      operator_id: '',
      recommendation: alarmGroup.llmRecommendation?.recommendation ?? '',
      summary: alarmGroup.llmRecommendation?.summary ?? '',
      headline: alarmGroup.llmRecommendation?.headline ?? '',
    };
  };

  const handleBulkSafe = () => {
    // Multi-grid is the low-priority queue by construction; only skip
    // groups that currently have an open escalation in progress.
    const allAlarmGroupIds = Object.keys(updatedAlarmGroupIdToDetailsMap);
    const bulkSafeEligibleAlarmGroupIds = allAlarmGroupIds.filter(
      (alarmGroupId) =>
        !alarmEscalationMap[alarmGroupId]?.currentAlarmEscalation
    );
    const skippedAlarmGroupIdCount =
      allAlarmGroupIds.length - bulkSafeEligibleAlarmGroupIds.length;

    if (skippedAlarmGroupIdCount > 0) {
      toast(
        `${skippedAlarmGroupIdCount} alarm group${
          skippedAlarmGroupIdCount === 1 ? ' has' : 's have'
        } an open escalation, Please resolve them individually`
      );
    }

    if (bulkSafeEligibleAlarmGroupIds.length === allAlarmGroupIds.length) {
      toast('All alarms are bulk resolved as safe');
    }

    handleSafeForAlarmGroupIds(bulkSafeEligibleAlarmGroupIds);
    bulkSafeEligibleAlarmGroupIds.forEach((alarmGroupId) => {
      onAlarmGroupAction(CamAction.SAFE, alarmGroupId, 'Bulk resolve', '', '');
    });
  };

  return (
    <div className="flex h-full flex-col">
      <BulkResolveBanner handleBulkSafe={handleBulkSafe} />
      <DynamicGrid
        ref={dynamicGridRef}
        maxCols={gridConfig.cols}
        maxRows={gridConfig.rows}
        useLastCell={true}
        renderItem={(alarmGroupId, isTopRightCorner) =>
          updatedAlarmGroupIdToDetailsMap[alarmGroupId] ? (
            <AlarmGroupCell
              alarmGroup={updatedAlarmGroupIdToDetailsMap[alarmGroupId]}
              onGroupAction={onAlarmGroupCellAction}
              isTopRightCorner={isTopRightCorner}
              escalationState={
                alarmEscalationMap[alarmGroupId]?.currentAlarmEscalation
              }
              llmEvent={getLLMEvent(alarmGroupId)}
              allowSafeOnEscalateRecommendation
            />
          ) : null
        }
      />
    </div>
  );
}
